import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { SessionOutputsListResponse } from "@/api/types";

export interface SessionAttachment {
  name: string;
  size: number;
  content_type: string;
}

export interface SessionAttachmentsPayload {
  items: SessionAttachment[];
  allowed_content_types: string[];
  max_bytes: number;
  /** Name of the per-session primary attachment (modify-intent
   *  edit target) or ``null`` when none is set. Surfaced alongside
   *  ``primary_attachment_missing`` so the UI can render a clear
   *  "primary not found on disk" warning when the session points
   *  at a file that's no longer in the attachments dir. */
  primary_attachment_name: string | null;
  /** ``true`` when ``primary_attachment_name`` is set but the file
   *  isn't in ``items`` — diagnostic for ``repo_root`` config
   *  drift, manual cleanup, or a soft-delete-then-DB-restore. */
  primary_attachment_missing: boolean;
}

export function useSessionAttachments(sessionId: string | null | undefined) {
  return useQuery({
    queryKey: keys.attachments(sessionId ?? ""),
    enabled: Boolean(sessionId),
    queryFn: async () =>
      (
        await api.get<SessionAttachmentsPayload>(
          `/sessions/${sessionId}/attachments`,
        )
      ).data,
  });
}

/** A file or directory in the session workspace, as the API names it:
 *  the root's ``id`` and the path relative to that root (``""`` for the
 *  root itself, ``/``-separated). Never a server path. */
export type WorkspaceRef = {
  root: string;
  path: string;
};

/** The ref of a draft (``drafts/<name>``): the drafts root's own file. */
export function draftWorkspaceRef(name: string): WorkspaceRef {
  return { root: "drafts", path: name };
}

export type WorkspaceRoot = {
  /** ``session``, ``drafts``, ``scratch``, ``worktree:<id>`` or
   *  ``readonly:<id>``: what list / file / download take as ``root``. */
  id: string;
  label: string;
  // "session" is the per-session workdir at <repo_root>/sessions/<sid>/
  // — attachments + outputs + (future) draft documents the agent edits.
  // Always listed first when present; persists across primary-repo
  // swaps so the agent's work isn't lost when a session reshapes its
  // repos. "drafts" is the docgen-internal draft state surface.
  kind: "session" | "worktree" | "scratch" | "readonly" | "drafts";
};

export type WorkspaceEntry = {
  name: string;
  // Human label when the on-disk name is opaque — a docgen draft's
  // ``<uuid>.json`` carries the draft's title here. Absent otherwise.
  label?: string;
  /** Relative to the listed root. */
  path: string;
  kind: "dir" | "file";
  size: number | null;
};

export type WorkspaceListing = {
  root: string;
  path: string;
  truncated: boolean;
  entries: WorkspaceEntry[];
};

export type WorkspaceFile = {
  root: string;
  path: string;
  size_bytes: number;
  truncated: boolean;
  binary: boolean;
  content: string;
};

/** Roots the agent can see for this session (primary + additional
 *  worktrees, plus the no-repo scratch dir for INTENTS_WITHOUT_REPO).
 *  Empty roots mean the session hasn't materialised anything yet —
 *  the SPA shows "no files yet" rather than 404'ing. */
export function useSessionWorkspaceRoots(sessionId: string | null) {
  return useQuery({
    queryKey: ["sessions", sessionId, "workspace", "roots"],
    enabled: Boolean(sessionId),
    queryFn: async () =>
      (
        await api.get<{ roots: WorkspaceRoot[] }>(
          `/sessions/${sessionId}/workspace`,
        )
      ).data,
    staleTime: 15_000,
  });
}

/** List entries in a workspace directory. Lazy-fired on tree
 *  expansion so deep traversal stays cheap. */
export function useWorkspaceListing(
  sessionId: string | null,
  dir: WorkspaceRef | null,
) {
  return useQuery({
    queryKey: ["sessions", sessionId, "workspace", "list", dir?.root, dir?.path],
    enabled: Boolean(sessionId && dir),
    queryFn: async () =>
      (
        await api.get<WorkspaceListing>(
          `/sessions/${sessionId}/workspace/list`,
          { params: { root: dir?.root, path: dir?.path } },
        )
      ).data,
    staleTime: 15_000,
  });
}

export function useWorkspaceFile(
  sessionId: string | null,
  file: WorkspaceRef | null,
) {
  return useQuery({
    queryKey: ["sessions", sessionId, "workspace", "file", file?.root, file?.path],
    enabled: Boolean(sessionId && file),
    queryFn: async () =>
      (
        await api.get<WorkspaceFile>(
          `/sessions/${sessionId}/workspace/file`,
          { params: { root: file?.root, path: file?.path } },
        )
      ).data,
    staleTime: 30_000,
  });
}

/** Build the absolute URL the browser hits to download a workspace
 *  file. Returns the same path the SPA renders inline via
 *  ``useWorkspaceFile``, but with ``Content-Disposition: attachment``
 *  on the response so the browser saves rather than navigates. */
export function workspaceDownloadUrl(
  sessionId: string,
  file: WorkspaceRef,
): string {
  const base = api.defaults.baseURL ?? "";
  const qs = new URLSearchParams({ root: file.root, path: file.path });
  return `${base}/sessions/${sessionId}/workspace/download?${qs.toString()}`;
}

export type SkillStatus = {
  skills_dir: string | null;
  items: { name: string; present: boolean }[];
};

/** Returns which anthropics/skills the deploy actually has on disk.
 *  The chat-turn flow's harness mounts each present skill into the
 *  per-session HOME; missing ones are logged + skipped, leaving
 *  modify-intent sessions without their key skill. The SPA reads
 *  this so the right-panel files card can render
 *  ``docx skill missing`` and the user knows to nudge the operator
 *  to rebuild the image. */
export function useSkillStatus() {
  return useQuery({
    queryKey: ["sessions", "skill-status"],
    queryFn: async () =>
      (await api.get<SkillStatus>("/sessions/skill-status")).data,
    staleTime: 60_000,
  });
}

/** Download a single session attachment by filename. Returns the
 *  blob + filename so the caller can pop a save dialog (anchor +
 *  ``download`` attribute) — same shape as ``useDownloadWorkDraft``.
 *  Used by the right-panel "Files in scope" card. */
export function useDownloadAttachment(sessionId: string) {
  return useMutation({
    mutationFn: async (
      name: string,
    ): Promise<{ blob: Blob; filename: string }> => {
      const resp = await api.get(
        `/sessions/${sessionId}/attachments/${encodeURIComponent(name)}`,
        { responseType: "blob" },
      );
      const disposition =
        (resp.headers["content-disposition"] as string | undefined) ?? "";
      const match =
        /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
      const filename = match?.[1] ? decodeURIComponent(match[1]) : name;
      return { blob: resp.data as Blob, filename };
    },
  });
}

export function useUploadAttachment(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post<SessionAttachment>(
        `/sessions/${sessionId}/attachments`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.attachments(sessionId) }),
  });
}

export function useDeleteAttachment(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      await api.delete(
        `/sessions/${sessionId}/attachments/${encodeURIComponent(name)}`,
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.attachments(sessionId) }),
  });
}

export function useSessionOutputs(sessionId: string | null | undefined) {
  return useQuery<SessionOutputsListResponse>({
    queryKey: keys.sessionOutputs(sessionId ?? ""),
    enabled: Boolean(sessionId),
    queryFn: async () =>
      (
        await api.get<SessionOutputsListResponse>(
          `/sessions/${sessionId}/outputs`,
        )
      ).data,
  });
}

export function useDeleteSessionOutput(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (outputId: string) => {
      await api.delete(`/sessions/${sessionId}/outputs/${outputId}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.sessionOutputs(sessionId) }),
  });
}
